import { BlockchainNetwork, FEATURES } from "../constants.js"

// EVM chains served by Bitquery GraphQL v2 (`EVM(network: …)`). They share
// one implementation (address stats, tracing, prices, Get API); this table
// holds what differs between them. Shared by backend and frontend, so keep it
// free of node-only imports.
export interface EvmV2Network {
    // Code stored with flows and in coinpath.state / reports
    // (blockchain_network is varchar(5))
    blockchain: BlockchainNetwork
    // Network name the app passes around: blockchain select value, `?network=`,
    // address stats key, moneyflow.network, explorer.bitquery.io path segment
    network: string
    // `EVM(network: …)` value
    v2Network: string
    title: string
    // Native coin symbol as v2 reports it; flows store native as this symbol
    nativeSymbol: string
    // Other names the native coin went by (Polygon's MATIC became POL)
    nativeAliases?: string[]
    // Wrapped native token: native has no DEX trades of its own, so it is
    // priced by this contract
    wrappedNative: string
    // The native coin is a USD stablecoin by design (Arc pays gas in USDC):
    // it and `wrappedNative` are priced at this fixed USD rate instead of
    // v2's AmountInUSD (which prices Arc's USDC like ETH) or the price index
    nativeUsdPeg?: number
    // Account feature flag the chain is limited to while in beta
    feature?: typeof FEATURES[keyof typeof FEATURES]
}

export const EVM_V2_NETWORKS: EvmV2Network[] = [
    {
        blockchain: BlockchainNetwork.ethereum,
        network: "ethereum",
        v2Network: "eth",
        title: "Ethereum Mainnet",
        nativeSymbol: "ETH",
        wrappedNative: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2",
    },
    {
        blockchain: BlockchainNetwork.bsc,
        network: "bsc",
        v2Network: "bsc",
        title: "BNB Smart Chain",
        nativeSymbol: "BNB",
        wrappedNative: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c",
    },
    {
        blockchain: BlockchainNetwork.matic,
        network: "matic",
        v2Network: "matic",
        title: "Polygon (Matic) Mainnet",
        nativeSymbol: "MATIC",
        nativeAliases: ["POL"],
        wrappedNative: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270",
    },
    {
        blockchain: BlockchainNetwork.arbitrum,
        network: "arbitrum",
        v2Network: "arbitrum",
        title: "Arbitrum One",
        nativeSymbol: "ETH",
        wrappedNative: "0x82af49447d8a07e3bd95bd0d56f35241523fbab1",
    },
    {
        blockchain: BlockchainNetwork.optimism,
        network: "optimism",
        v2Network: "optimism",
        title: "Optimism (OP Mainnet)",
        nativeSymbol: "ETH",
        wrappedNative: "0x4200000000000000000000000000000000000006",
    },
    {
        blockchain: BlockchainNetwork.base,
        network: "base",
        v2Network: "base",
        title: "Base",
        nativeSymbol: "ETH",
        wrappedNative: "0x4200000000000000000000000000000000000006",
    },
    {
        blockchain: BlockchainNetwork.robinhood,
        network: "robinhood",
        v2Network: "robinhood",
        title: "Robinhood Chain",
        nativeSymbol: "ETH",
        wrappedNative: "0x0bd7d308f8e1639fab988df18a8011f41eacad73",
    },
    {
        blockchain: BlockchainNetwork.arc,
        network: "arc",
        v2Network: "arc",
        title: "Arc",
        // Native USDC (18 decimals); 0x3600… is its ERC-20 interface
        // (6 decimals) over the same balance, reported as separate transfers
        nativeSymbol: "USDC",
        wrappedNative: "0x3600000000000000000000000000000000000000",
        nativeUsdPeg: 1,
    },
]

// Accepts a blockchain code ("eth", "arb") or a network name ("ethereum",
// "arbitrum").
export const getEvmV2Network = (networkOrBlockchain: string | undefined): EvmV2Network | undefined =>
    EVM_V2_NETWORKS.find((n) => n.network === networkOrBlockchain || n.blockchain === networkOrBlockchain)

export const isEvmV2 = (networkOrBlockchain: string | undefined) => !!getEvmV2Network(networkOrBlockchain)
